import React, { useState } from 'react';

interface EditColumnsProps {
  /** Main (left) pane — the primary form or widget. */
  children: React.ReactNode;
  /** Side (right) pane — auxiliary widgets/sections. */
  side: React.ReactNode;
  /** Mobile switcher label for the main pane. */
  mainLabel: string;
  /** Mobile switcher label for the side pane. */
  sideLabel: string;
  /** Which pane the mobile switcher starts on. Defaults to the main pane. */
  defaultPane?: 'main' | 'side';
}

/**
 * Responsive two-column edit-page layout that folds automatically.
 *
 * Desktop (>64rem): main + side sit in a `2fr 1fr` grid.
 * 48–64rem:         single column, both panes stacked (side below main).
 * ≤48rem:           only one pane shows at a time; a mobile-only tab switcher
 *                   toggles between them.
 *
 * This is the systemic version of the pattern previously hand-wired into each
 * edit page (the `mobilePane` state + `.tab-switcher.mobile-only` + `.active`
 * toggling). It reuses the existing `.edit-page-*` CSS in App.css, so pages that
 * adopt it look identical to the original course-edit layout. Host it inside a
 * full-width shell (`.admin-page-content`), NOT the width-capped
 * `.admin-form-page`, or the grid won't have room to sit side by side.
 */
const EditColumns: React.FC<EditColumnsProps> = ({
  children,
  side,
  mainLabel,
  sideLabel,
  defaultPane = 'main',
}) => {
  const [pane, setPane] = useState<'main' | 'side'>(defaultPane);

  return (
    <>
      {/* Mobile-only sub-pane switch (hidden >48rem via .mobile-only). */}
      <div className="tab-switcher mobile-only">
        <button
          type="button"
          className={pane === 'main' ? 'active' : ''}
          onClick={() => setPane('main')}
        >
          {mainLabel}
        </button>
        <button
          type="button"
          className={pane === 'side' ? 'active' : ''}
          onClick={() => setPane('side')}
        >
          {sideLabel}
        </button>
      </div>

      <div className="edit-page-layout">
        <div className={`edit-page-form ${pane === 'main' ? 'active' : ''}`}>{children}</div>
        <div className={`edit-page-widgets ${pane === 'side' ? 'active' : ''}`}>{side}</div>
      </div>
    </>
  );
};

export default EditColumns;
